/**
 * Admin token storage — DIFFERS from mobile's Keychain-backed module by
 * design.
 *
 * Threat model & rationale (LOCK 3):
 * - **Access token: in-memory** via a module-level closure. The token
 *   is never persisted; a page reload (F5, tab close) loses it and the
 *   session re-rebuilds via the refresh token. Rationale — admin's
 *   browser context has no native secure-storage primitive comparable
 *   to iOS Keychain / Android Keystore. localStorage is XSS-readable
 *   across same-origin scripts; in-memory survives only the current
 *   document lifetime.
 * - **Refresh token: `sessionStorage`** — survives F5 within the tab
 *   so the operator doesn't re-OTP on every reload, but disappears on
 *   tab close. Tab-scoped XSS surface is acceptable for v1: admin user
 *   count is small (≤20), the panel is internal-only, and CSP +
 *   reviewed deps keep the XSS bar high.
 *
 * Future migration: move the refresh token to an httpOnly cookie set
 * by the API on `/auth/refresh`. Backend support lands as a follow-up
 * (ADMIN-12+); this module is the seam — swap getRefreshToken /
 * setTokens to no-ops and the cookie carries the value automatically.
 */

let accessToken: string | null = null;

/** sessionStorage key — namespace with `admin_` so other apps on the
 *  same origin don't collide. */
const REFRESH_KEY = 'admin_refresh_token';

export const getAccessToken = (): string | null => accessToken;

export const getRefreshToken = (): string | null => {
  try {
    return window.sessionStorage.getItem(REFRESH_KEY);
  } catch {
    // sessionStorage can throw in private-browsing edge cases; treat as no token.
    return null;
  }
};

export const setTokens = (input: { accessToken: string; refreshToken: string }): void => {
  accessToken = input.accessToken;
  try {
    window.sessionStorage.setItem(REFRESH_KEY, input.refreshToken);
  } catch {
    // Silent — caller is already authed in-memory; refresh just won't survive reload.
  }
};

export const clearTokens = (): void => {
  accessToken = null;
  try {
    window.sessionStorage.removeItem(REFRESH_KEY);
  } catch {
    // Silent — nothing to do.
  }
};

/** Test seam — reset the closure between specs so isolation holds. */
export const _resetTokenStorageForTests = (): void => {
  accessToken = null;
  try {
    window.sessionStorage.removeItem(REFRESH_KEY);
  } catch {
    /* silent */
  }
};
